import { addDays, startOfToday, parseISO, formatISO } from 'date-fns';
import * as React from 'react';

import { getButtonLabel } from './utils';
import { checkIsHotelsSearch } from '../../../../../../shared/utils/checkIsHotelsSearch';
import { getDateRangeValue } from '../../../../../JsonQuery';
import { Dates } from '../../../components/filters/Dates';
import { IFilterComponentProps } from '../../../types/filters';
import { useSearchResultsTooltipOpen } from '../../../utils/searchResultsTooltipOpen';
import { useContext } from '../../../utils/useContext';

export const DatesContainer: React.FC<IFilterComponentProps> = ({ filterKey, open, onOpen, onClose }) => {
  const { jsonQuery, jsonQueryCount, jsonQueryCountRefreshing, onChange, onApply, onApplyClick } = useContext();

  const isHotelSearch = checkIsHotelsSearch(jsonQuery);
  const range = getDateRangeValue('dates')(jsonQuery);
  const openTooltip = useSearchResultsTooltipOpen({
    filterKey,
    actions: ['setDates'],
  });

  const today = startOfToday();
  const minDate = today;
  const maxDate = addDays(today, 365);
  const fromValue = range?.gte ? parseISO(range.gte) : null;
  const toValue = range?.lt ? parseISO(range.lt) : null;

  const buttonLabel = getButtonLabel(fromValue, toValue);

  const handleApply = React.useCallback(() => {
    if (onApplyClick) {
      onApplyClick({ source: 'tooltip' });
    }

    onApply();

    if (onClose) {
      onClose();
    }
  }, [onApply, onApplyClick, onClose]);

  const handleDatesChange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      let fromValue = from;
      let toValue = to;

      if (fromValue && !toValue) {
        toValue = addDays(fromValue, 1);
      }
      if (toValue && !fromValue) {
        fromValue = addDays(toValue, -1);
      }
      if (fromValue && toValue && fromValue >= toValue) {
        toValue = addDays(fromValue, 1);
      }

      if (fromValue && toValue) {
        const gte = formatISO(fromValue, { representation: 'date' });
        const lte = formatISO(toValue, { representation: 'date' });

        onChange({ action: 'setDates', arguments: [gte, lte] });
      }
    },
    [onChange],
  );

  return (
    <Dates
      isOpen={open}
      buttonLabel={buttonLabel}
      minDate={minDate}
      maxDate={maxDate}
      fromValue={fromValue}
      toValue={toValue}
      offersCount={isHotelSearch ? 0 : jsonQueryCount}
      offersCountLoading={jsonQueryCountRefreshing}
      openTooltip={openTooltip}
      onButtonClick={onOpen}
      onDatesChange={handleDatesChange}
      onApply={handleApply}
      onClose={onClose}
    />
  );
};
